import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  fetchAllOrganizations,
  fetchMyOrganizations,
  fetchOrganizationBySlug,
  fetchOrgReadme,
  updateOrgReadme as updateOrgReadmeApi,
  assignOrgToMe,
  unassignOrgFromMe,
  fetchOrgUsers,
} from '_/api/organizations/organizations.api';
import { useAuth } from '_/contexts/AuthContext';
import { IOrganization, IOrgUser } from '_/types/organizations/organization.types';
import { notification } from 'antd';

export const ALL_ORGANIZATIONS_QUERY_KEY = ['organizations', 'all'];
export const MY_ORGANIZATIONS_QUERY_KEY = ['organizations', 'my'];
export const ORGANIZATION_QUERY_KEY = (orgSlug: string): string[] => ['organization', orgSlug];
export const ORG_README_QUERY_KEY = (orgId: string): (string | number)[] => ['organization', orgId, 'readme'];
export const ORG_USERS_QUERY_KEY = (orgId: string): string[] => ['organization', orgId, 'users'];

export const useAllOrganizations = (enabled = true): UseQueryResult<IOrganization[]> => {
  return useQuery({
    queryKey: ALL_ORGANIZATIONS_QUERY_KEY,
    queryFn: fetchAllOrganizations,
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useMyOrganizations = (): UseQueryResult<IOrganization[]> => {
  return useQuery({
    queryKey: MY_ORGANIZATIONS_QUERY_KEY,
    queryFn: fetchMyOrganizations,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useOrganization = (orgSlug: string | undefined): UseQueryResult<IOrganization | null> => {
  return useQuery({
    queryKey: ORGANIZATION_QUERY_KEY(orgSlug || ''),
    queryFn: () => fetchOrganizationBySlug(orgSlug!),
    enabled: !!orgSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useAssignOrgToMe = (): UseMutationResult<void, Error, IOrganization> => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  if (!userId) {
    throw new Error('User not found');
  }

  return useMutation({
    mutationFn: (org: IOrganization) => assignOrgToMe({ org: org, userId: userId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ALL_ORGANIZATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: MY_ORGANIZATIONS_QUERY_KEY });
      notification.success({
        message: 'Organization assigned',
      });
    },
    onError: () => {
      notification.error({
        message: 'An unknown error occurred',
      });
    },
  });
};

export const useUnassignOrgFromMe = (): UseMutationResult<void, Error, IOrganization> => {
  const queryClient = useQueryClient();
  const { data: myOrganizations = [] } = useMyOrganizations();

  return useMutation({
    mutationFn: (org: IOrganization) => {
      let orgToUnassign = org;

      // If org doesn't have roles, find it in myOrganizations
      if (!org.roles) {
        const myOrg = myOrganizations.find((o: IOrganization) => o.id === org.id);
        if (!myOrg) {
          throw new Error('User does not belong to org');
        }
        orgToUnassign = myOrg;
      }

      if (!orgToUnassign.roles?.id) {
        throw new Error('Organization role ID not found');
      }

      return unassignOrgFromMe({
        orgSlug: orgToUnassign.slug,
        roleId: orgToUnassign.roles.id,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ALL_ORGANIZATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: MY_ORGANIZATIONS_QUERY_KEY });
      notification.success({
        message: 'Organization unassigned',
      });
    },
    onError: (error: any) => {
      console.error(error);
      notification.error({
        message: error?.message || 'An unknown error occurred',
      });
    },
  });
};

export const useOrgReadme = (orgId: string | undefined): UseQueryResult<string> => {
  return useQuery({
    queryKey: ORG_README_QUERY_KEY(orgId || ''),
    queryFn: () => fetchOrgReadme(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateOrgReadme = (): UseMutationResult<string, Error, { orgId: string; content: string }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, content }: { orgId: string; content: string }) => {
      return updateOrgReadmeApi(orgId, content);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ORG_README_QUERY_KEY(variables.orgId) });
      notification.success({
        message: 'Organization Readme Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating readme:', error);
      notification.error({
        message: 'Error updating organization readme',
      });
    },
  });
};

export const useOrgUsers = (orgId: string | undefined): UseQueryResult<IOrgUser[]> => {
  return useQuery<IOrgUser[]>({
    queryKey: ORG_USERS_QUERY_KEY(orgId || ''),
    queryFn: () => fetchOrgUsers(orgId!),
    enabled: !!orgId,
    staleTime: 30 * 1000, // 30 seconds - users change frequently
  });
};
